import type { DscApplicantSection } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { type Resolver, useFieldArray, useForm } from "react-hook-form";

import { mapBankSectionToBankAccount } from "./bankMapping";
import { mapIncomeSectionToIncomes } from "./incomeMapping";
import { deriveApplicantSsnType, mapResidenceSectionToCustomerInfo } from "./residenceMapping";
import { makeEmptyBankAccount, makeEmptyCustomerInfo, type SignersInput, signersSchema } from "./Signers.schema";

// ---------------------------------------------------------------------------
// useSignersForm
//
// Owns all RHF state for the Signers card. Returns the form instance and the
// two field-array instances (driver + secondSigner incomes) so Signers.tsx and
// SignerColumn stay presenter-only.
//
// Presence is derived from `present` in the dashboard response. Income blocks
// are hydrated from each applicant's `income.employments` (see incomeMapping);
// an applicant with no employments seeds exactly ONE blank income block. The
// bank account is hydrated from `bank.bankAccounts` (see bankMapping), and
// customer info from `residence` (see residenceMapping).
// ---------------------------------------------------------------------------

interface UseSignersFormArguments {
  /** Primary applicant (driver) section from the dashboard response. */
  driver?: DscApplicantSection;
  /** Second signer (cosigner) section from the dashboard response. */
  secondSigner?: DscApplicantSection;
}

/**
 * @param {UseSignersFormArguments} args - Driver/2nd-signer sections from the dashboard response.
 * @returns {object} RHF form instance and the driver/2nd-signer income field-array helpers.
 */
export function useSignersForm({ driver, secondSigner }: UseSignersFormArguments = {}) {
  const hasSecondSigner = Boolean(secondSigner?.present);

  // SSN/ITIN status per applicant — gates each column's Income Type list and
  // clamps hydrated income types (see incomeMapping / IncomeBlock).
  const driverSsnType = deriveApplicantSsnType(driver?.residence);
  const secondSignerSsnType = deriveApplicantSsnType(secondSigner?.residence);

  const form = useForm<SignersInput>({
    // zodResolver infers the schema's INPUT type: because several fields use
    // .default() and the schema is a ZodEffects (via .superRefine), defaulted
    // fields become `string | undefined` and diverge from SignersInput (the
    // output type the form is typed with). Cast to the form's resolver type —
    // runtime behavior is unchanged (it still validates and returns output).
    resolver: zodResolver(signersSchema) as unknown as Resolver<SignersInput>,
    mode: "onBlur",
    defaultValues: {
      driver: {
        incomes: mapIncomeSectionToIncomes(driver?.income, driverSsnType),
        bankAccount: mapBankSectionToBankAccount(driver?.bank),
        customerInfo: mapResidenceSectionToCustomerInfo(driver?.residence),
      },
      // Hydrate the 2nd signer's incomes only when the response says one is
      // present; otherwise leave an empty incomes array so useFieldArray can
      // register cleanly while the column stays hidden.
      // TODO(OR-22091 follow-up): when submit is wired to the BFF, an absent
      // 2nd signer here (`incomes: []`) will fail `personSchema`'s `incomes.min(1)`
      // because `secondSigner` is non-undefined. At that point either seed the
      // absent 2nd signer as `undefined` (matching `.optional()`) or gate
      // validation on presence. Masked today only because onSubmit is a no-op.
      secondSigner: hasSecondSigner
        ? {
            incomes: mapIncomeSectionToIncomes(secondSigner?.income, secondSignerSsnType),
            bankAccount: mapBankSectionToBankAccount(secondSigner?.bank),
            customerInfo: mapResidenceSectionToCustomerInfo(secondSigner?.residence),
          }
        : { incomes: [], bankAccount: makeEmptyBankAccount(), customerInfo: makeEmptyCustomerInfo() },
    },
  });

  const { control } = form;

  // Both field arrays are always mounted (React hook rules); the parent decides
  // which columns to render based on the response.
  const driverIncomes = useFieldArray({
    control,
    name: "driver.incomes",
  });

  const secondSignerIncomes = useFieldArray({
    control,
    name: "secondSigner.incomes",
  });

  return {
    form,
    control,
    driverIncomes,
    secondSignerIncomes,
    driverSsnType,
    secondSignerSsnType,
  };
}
